'use client';

import {
  useCallback,
  useRef,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';

export type TabsVariant = 'underline' | 'segmented';

export interface TabDescriptor<TKey extends string> {
  key: TKey;
  label: ReactNode;
  /** Rendered after the label — a count badge, a status dot. */
  adornment?: ReactNode;
}

export interface TabsProps<TKey extends string> {
  tabs: ReadonlyArray<TabDescriptor<TKey>>;
  activeKey: TKey;
  onChange: (key: TKey) => void;
  /** Names the tablist for screen readers. Required: an unlabelled tablist is a WCAG failure. */
  ariaLabel: string;
  variant?: TabsVariant;
  /**
   * Prefix for the generated `id`s. The matching panel must carry
   * `id={tabPanelId(idPrefix, key)}` and `aria-labelledby={tabId(idPrefix, key)}`.
   */
  idPrefix: string;
  className?: string;
}

export const tabId = (idPrefix: string, key: string) => `${idPrefix}-${key}-tab`;
export const tabPanelId = (idPrefix: string, key: string) =>
  `${idPrefix}-${key}-panel`;

const listStyles: Record<TabsVariant, string> = {
  underline: '-mb-px flex flex-wrap gap-x-6 overflow-x-auto',
  segmented: 'grid auto-cols-fr grid-flow-col gap-2',
};

/*
 * A tab is part of a composite widget, not an action, so the underline variant
 * styles a bare <button> rather than fighting the pill primitive's shape — the
 * exception AGENTS.md § Button Component Usage carves out. The segmented variant
 * *is* a row of pills, so it uses the primitive directly.
 */
const underlineTabStyles = (isActive: boolean) =>
  cn(
    'whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium transition-colors duration-150',
    'inline-flex items-center gap-2',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    isActive
      ? 'border-accent text-accent'
      : 'border-transparent text-text-secondary hover:border-secondary hover:text-text-primary'
  );

/**
 * An accessible tab strip.
 *
 * Beyond deduplicating four hand-rolled copies, this adds the keyboard behaviour the
 * ARIA tabs pattern requires and none of those copies had: a roving tabindex, so the
 * strip is a single tab stop, and arrow/Home/End keys move between tabs.
 */
export function Tabs<TKey extends string>({
  tabs,
  activeKey,
  onChange,
  ariaLabel,
  variant = 'underline',
  idPrefix,
  className,
}: TabsProps<TKey>) {
  const listRef = useRef<HTMLDivElement | null>(null);

  const focusTabAt = useCallback(
    (index: number) => {
      const bounded = (index + tabs.length) % tabs.length;
      const next = tabs[bounded];
      if (!next) {
        return;
      }

      onChange(next.key);
      listRef.current
        ?.querySelector<HTMLElement>(`#${CSS.escape(tabId(idPrefix, next.key))}`)
        ?.focus();
    },
    [idPrefix, onChange, tabs]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      const current = tabs.findIndex(tab => tab.key === activeKey);
      if (current < 0) {
        return;
      }

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault();
          focusTabAt(current + 1);
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          focusTabAt(current - 1);
          break;
        case 'Home':
          event.preventDefault();
          focusTabAt(0);
          break;
        case 'End':
          event.preventDefault();
          focusTabAt(tabs.length - 1);
          break;
        default:
          break;
      }
    },
    [activeKey, focusTabAt, tabs]
  );

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={ariaLabel}
      className={cn(listStyles[variant], className)}
    >
      {tabs.map(tab => {
        const isActive = tab.key === activeKey;
        const shared = {
          id: tabId(idPrefix, tab.key),
          role: 'tab' as const,
          'aria-selected': isActive,
          'aria-controls': tabPanelId(idPrefix, tab.key),
          tabIndex: isActive ? 0 : -1,
          onClick: () => onChange(tab.key),
          // On the tab, not the tablist: the list itself is never focusable, so a
          // handler there would only ever fire by bubbling anyway.
          onKeyDown: handleKeyDown,
        };

        if (variant === 'segmented') {
          return (
            <Button
              key={tab.key}
              {...shared}
              type="button"
              variant={isActive ? 'accent' : 'default'}
            >
              {tab.label}
              {tab.adornment}
            </Button>
          );
        }

        return (
          <button
            key={tab.key}
            {...shared}
            type="button"
            className={underlineTabStyles(isActive)}
          >
            {tab.label}
            {tab.adornment}
          </button>
        );
      })}
    </div>
  );
}

export default Tabs;
